/**
 * QuoteRequestDialog — the public "Request a Quote" form for the
 * Enterprise tier (and any future custom-priced offer).
 *
 * Designed to be openable from anywhere (Pricing page CTA, Billing UI
 * upgrade row, marketing modals) so it lives as a self-contained dialog
 * with its own form state.
 *
 * Persists via POST /api/billing/quote-request — a public endpoint, no
 * auth required. Backend stores in `db.quote_requests` and forwards to
 * SALES_QUOTE_INBOX (env var, currently no-op until iter 131 wires a real
 * email provider — the form copy reflects that ETA).
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const ENTERPRISE_FEATURES = [
  { key: 'white_label', label: 'White-label / custom branding' },
  { key: 'carrier_portal', label: 'Carrier portal' },
  { key: 'webhook_integrations', label: 'Webhook integrations' },
  { key: 'saml_sso', label: 'SAML SSO' },
  { key: 'api_access', label: 'Public API access' },
  { key: 'compliance', label: 'Compliance (HACCP / GxP)' },
  { key: 'audit_log', label: 'Audit log' },
  { key: 'data_health', label: 'Data Health admin' },
];

export default function QuoteRequestDialog({ open, onOpenChange, prefillFeatures = [] }) {
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    expected_users: '',
    expected_skus: '',
    expected_orders_per_month: '',
    expected_warehouses: '',
    message: '',
  });
  const [features, setFeatures] = useState(prefillFeatures);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const reset = () => {
    setForm({
      company_name: '', contact_name: '', email: '', phone: '',
      expected_users: '', expected_skus: '',
      expected_orders_per_month: '', expected_warehouses: '',
      message: '',
    });
    setFeatures(prefillFeatures);
    setSubmitted(null);
  };

  const toggleFeature = (k) => {
    setFeatures((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]));
  };

  const submit = async () => {
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error('Company, contact name and email are required.');
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/api/billing/quote-request`, {
        ...form,
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        requested_features: features,
      });
      setSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send quote request — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (next) => {
    onOpenChange(next);
    if (!next) {
      // Defer state reset slightly so the closing animation doesn't show
      // empty fields mid-transition.
      setTimeout(reset, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="border border-border rounded-none max-w-2xl" data-testid="quote-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="duotone" className="text-emerald-700" />
            Request an Enterprise quote
          </DialogTitle>
          <DialogDescription className="text-xs">
            Tell us about your operation and a rep will get back to you within one business day with a tailored quote.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center space-y-3" data-testid="quote-submitted">
            <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-600" />
            <h3 className="text-lg font-bold">Got it — we'll be in touch.</h3>
            <p className="text-sm text-muted-foreground">
              Your quote request <span className="font-mono">#{submitted.id?.slice(-8)}</span> has been logged.
              {submitted.email_forwarded
                ? ' Sales has been notified.'
                : ' A rep will pick it up from the queue shortly.'}
            </p>
            <Button onClick={() => handleClose(false)} className="rounded-none" data-testid="quote-close-btn">Close</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-[10px]">Company *</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="quote-company-input" />
              </div>
              <div>
                <Label className="overline text-[10px]">Contact name *</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="quote-contact-input" />
              </div>
              <div>
                <Label className="overline text-[10px]">Work email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email-input" />
              </div>
              <div>
                <Label className="overline text-[10px]">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-phone-input" />
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Workload sizing (optional)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label className="overline text-[10px]">Users</Label>
                  <Input value={form.expected_users} onChange={(e) => setForm({ ...form, expected_users: e.target.value })} placeholder="e.g. 50" />
                </div>
                <div>
                  <Label className="overline text-[10px]">SKUs</Label>
                  <Input value={form.expected_skus} onChange={(e) => setForm({ ...form, expected_skus: e.target.value })} placeholder="e.g. 25,000" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Orders/mo</Label>
                  <Input value={form.expected_orders_per_month} onChange={(e) => setForm({ ...form, expected_orders_per_month: e.target.value })} placeholder="e.g. 80,000" />
                </div>
                <div>
                  <Label className="overline text-[10px]">Warehouses</Label>
                  <Input value={form.expected_warehouses} onChange={(e) => setForm({ ...form, expected_warehouses: e.target.value })} placeholder="e.g. 3" />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Features I'm interested in</div>
              <div className="grid grid-cols-2 gap-1.5">
                {ENTERPRISE_FEATURES.map((f) => (
                  <label
                    key={f.key}
                    className={`text-xs flex items-center gap-2 px-2 py-1.5 border cursor-pointer transition ${
                      features.includes(f.key) ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:bg-muted'
                    }`}
                    data-testid={`quote-feature-${f.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={features.includes(f.key)}
                      onChange={() => toggleFeature(f.key)}
                      className="shrink-0"
                    />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="overline text-[10px]">Anything else?</Label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                placeholder="Tell us about your specific requirements, integration needs, compliance requirements, etc."
                className="w-full mt-1 p-2 border border-border bg-background text-sm rounded-none font-sans resize-y"
                data-testid="quote-message-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" onClick={() => handleClose(false)} className="rounded-none" data-testid="quote-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
                {submitting ? 'Sending…' : 'Send quote request'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
